import { Inject, Injectable } from '@angular/core';
import { Result } from '../models';
import { ENV, IEnvironment } from 'env/config';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Service } from '../entity';

import { DataService } from '../http/dataService';
import { CentralApis } from '../interfaces/internal';
import { Fsa, ITerritoryFsa } from '../models/fsa.model';
import { Territory } from '../models/territory.model';
import { Repository } from './repository';
import { TerritoryType } from '../lib/common';
import { MyWorldService } from './myWorld';
import { IGeoServer } from '../interfaces/models';
import { TerritoryService } from './territory';

interface IFsaQuery {
  query: string;
  coidOrCity: string;
  territoryId: string;
  territoryType: string;
  listOfFsas: ITerritoryFsa[];
  ignoreBlankFsaAssigned?: boolean;
  territories: Observable<Result<Territory>>;
}

export interface IFsaCondition {
  ignoreBlankFsaAssigned?: boolean;
  territoryId: string;
  territoryType: string;
}

@Service({
  api: 'fsa',
  model: Fsa,
})
@Injectable({ providedIn: 'root' })
export class FsaService extends Repository<Fsa> {
  private fsaField = 'attr11';
  private pfsaField = 'cfsauid';
  private coidField = 'attr15';
  private cityField = 'city_name';

  constructor(
    @Inject(ENV) env: IEnvironment,
    dataService: DataService,
    private myWorldService: MyWorldService,
    private territoryService: TerritoryService
  ) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).territory,
    });
  }

  getAll(queryParams?, conditions?: IFsaCondition) {
    const ignoreBlankFsaAssigned = conditions?.ignoreBlankFsaAssigned;
    const { territoryType, territoryId } = conditions;

    return super.getAll(queryParams).pipe(
      switchMap((fsas) => {
        const listOfFsas: ITerritoryFsa[] = [...fsas.items] as any;
        // Total FSA assigned for a particular territory ID
        const totalFsaAssigned = territoryId
          ? listOfFsas.filter((fsa) => fsa.territoryId === territoryId).length
          : 0;

        // Getting a list of territories assigned to an each FSA
        const territoryIds = [
          ...new Set(
            listOfFsas
              .filter((fsa) => fsa.territoryId !== territoryId)
              .map((fsa) => fsa.territoryId)
          ),
        ];
        return this.getMyWorldData({
          // 'not in' this is used to ignore the fsas that has been already assigned
          // to this territory (territoryId)
          query: `not in (${listOfFsas
            .filter((fsa) => fsa.territoryId === territoryId)
            .map((fsa) => `'${fsa.fsa}'`)
            .join()})`,
          coidOrCity: queryParams.coid,
          // If totalFsaAssigned is 0 for a particular territory Id
          // we don't care for this value, we want all the data from geoServer
          territoryId: totalFsaAssigned > 0 ? territoryId : null,
          territoryType: territoryType.toLowerCase(),
          listOfFsas,
          ignoreBlankFsaAssigned,
          territories:
            territoryIds.length > 0
              ? this.territoryService.getAll({ ids: territoryIds.join() })
              : of({} as Result<Territory>),
        });
      }),
      map((result) => {
        const fsas = result[0];
        const myWorld = result[1];
        const territories = result[2]?.items ?? [];

        return {
          meta: myWorld?.meta,
          links: myWorld?.links,
          items:
            fsas?.length > 0 || ignoreBlankFsaAssigned
              ? myWorld.items.map((item) =>
                  Fsa.createFromDTO(item, territories, fsas)
                )
              : [],
        };
      })
    );
  }

  getByTerritory(queryParams?, conditions?): Observable<Result<Fsa>> {
    const territory: Territory = conditions.territory;

    return super.getAll(queryParams).pipe(
      switchMap((fsas) => {
        const listOfFsas: ITerritoryFsa[] = [...fsas.items] as any;

        return this.getMyWorldData({
          // this method is used to load the FSAs in the territory detail page
          // 'in' is used to only get the fsas assigne to show them on the detail page
          query: `in (${listOfFsas.map((fsa) => `'${fsa.fsa}'`).join()})`,
          coidOrCity: territory.territoryFor,
          territoryId: territory.id.toString(),
          territoryType: territory.territoryType.toLowerCase(),
          listOfFsas,
          territories: of({ items: [territory], links: null, meta: null }),
        });
      }),
      map((result) => {
        const fsas = result[0];
        const myWorld = result[1];

        return {
          meta: myWorld.meta,
          links: myWorld.links,
          items:
            fsas.length > 0
              ? myWorld.items.map((item) =>
                  Fsa.createFromDTO(item, [territory], fsas)
                )
              : [],
        };
      })
    );
  }

  private getMyWorldData(fsaQuery: IFsaQuery) {
    const {
      territoryType,
      listOfFsas,
      coidOrCity,
      territories,
      query,
      territoryId,
      ignoreBlankFsaAssigned,
    } = fsaQuery;
    let cqlFilter = '';
    let sortBy = '';
    let typeName = '';

    if (territoryType === TerritoryType.coAndFsa.toLowerCase()) {
      // If territoryId is null then we just want to filter by coid
      cqlFilter = territoryId
        ? `${this.fsaField} ${query} and ${this.coidField} = '${coidOrCity}'`
        : `${this.coidField} = '${coidOrCity}'`;
      sortBy = this.fsaField;
      typeName = 'telus:fdsa_polygon';
    } else {
      // If territoryId is null then we just want to filter by city
      cqlFilter = territoryId
        ? `${this.pfsaField} ${query} and ${this.cityField} = '${coidOrCity}'`
        : `${this.cityField} = '${coidOrCity}'`;
      sortBy = this.pfsaField;
      typeName = 'telus:postal_fsa_boundaries';
    }

    return forkJoin([
      of(listOfFsas),
      listOfFsas.length > 0 || ignoreBlankFsaAssigned
        ? this.myWorldService.getFsasByCoidOrCity({
            cqlFilter,
            sortBy,
            typeName,
          })
        : of({} as Result<IGeoServer>),
      territories,
    ]);
  }
}
